// apps/scheduler-worker/src/components/TaskActionButtons.tsx
import { Play, Pause, CheckCircle, Loader2, QrCode, WifiOff, AlertCircle } from 'lucide-react';
import { Button } from 'scheduler-ui';
import type { TaskStatus } from 'scheduler-types';
import { SerialNumberDisplay } from './SerialNumberDisplay';

interface TaskActionButtonsProps {
  status: TaskStatus;
  onStart: () => void;
  onStop: () => void;
  onComplete: () => void;
  isStarting: boolean;
  isStopping: boolean;
  isCompleting: boolean;
  disabled?: boolean;
  // Serialization props
  isSerialized?: boolean;
  serialNumber?: string | null;
  partNumber?: string;
  onStartSerialized?: () => void;
  isStartingSerialized?: boolean;
  isOnline?: boolean;
  serializationError?: string | null;
}

export function TaskActionButtons({
  status,
  onStart,
  onStop,
  onComplete,
  isStarting,
  isStopping,
  isCompleting,
  disabled = false,
  // Serialization props
  isSerialized = false,
  serialNumber = null,
  partNumber,
  onStartSerialized,
  isStartingSerialized = false,
  isOnline = true,
  serializationError = null,
}: TaskActionButtonsProps) {
  const isLoading = isStarting || isStopping || isCompleting || isStartingSerialized;

  // Task is ready to start
  if (status === 'ready' || status === 'staged') {
    // Serialized task - requires connectivity
    if (isSerialized) {
      return (
        <div className="flex flex-col gap-3">
          {/* Offline warning for serialized tasks */}
          {!isOnline && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <WifiOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Connectivity Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  Serialized builds require an internet connection to assign a serial number.
                  Please connect to the network to start this task.
                </p>
              </div>
            </div>
          )}

          {/* Serialization error */}
          {serializationError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Failed to Assign Serial Number</p>
                <p className="text-sm text-red-700 mt-1">{serializationError}</p>
              </div>
            </div>
          )}

          <Button
            size="lg"
            className="w-full h-14 text-lg gap-2 bg-purple-600 hover:bg-purple-700"
            onClick={onStartSerialized}
            disabled={disabled || isLoading || !isOnline}
          >
            {isStartingSerialized ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <QrCode className="w-5 h-5" />
            )}
            Assign Serial & Start
          </Button>
          <p className="text-xs text-center text-gray-500">
            A unique serial number will be assigned to this unit
          </p>
        </div>
      );
    }

    // Regular (non-serialized) task
    return (
      <div className="flex flex-col gap-3">
        <Button
          size="lg"
          className="w-full h-14 text-lg gap-2"
          onClick={onStart}
          disabled={disabled || isLoading}
        >
          {isStarting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Play className="w-5 h-5" />
          )}
          Start Task
        </Button>
      </div>
    );
  }

  // Task is in progress - show stop and complete buttons
  if (status === 'in_progress') {
    return (
      <div className="flex flex-col gap-3">
        {/* Show serial number if this is a serialized task */}
        {isSerialized && serialNumber && (
          <SerialNumberDisplay serialNumber={serialNumber} partNumber={partNumber} />
        )}

        <Button
          size="lg"
          className="w-full h-14 text-lg gap-2 bg-green-600 hover:bg-green-700"
          onClick={onComplete}
          disabled={disabled || isLoading}
        >
          {isCompleting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          Complete Task
        </Button>
        <Button
          size="lg"
          variant="secondary"
          className="w-full h-14 text-lg gap-2"
          onClick={onStop}
          disabled={disabled || isLoading}
        >
          {isStopping ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Pause className="w-5 h-5" />
          )}
          Pause
        </Button>
      </div>
    );
  }

  // Task is complete
  if (status === 'complete') {
    return (
      <div className="flex flex-col gap-3">
        {/* Show serial number for completed serialized tasks */}
        {isSerialized && serialNumber && (
          <SerialNumberDisplay serialNumber={serialNumber} partNumber={partNumber} compact />
        )}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-green-800 font-medium">Task Complete</p>
        </div>
      </div>
    );
  }

  // Task is pending or blocked - cannot start
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
      <p className="text-gray-600">Task not ready to start</p>
    </div>
  );
}
